import { useForm } from "@inertiajs/react"
import type { FormEvent } from "react"

import Layout from "../../../shared/Layout"
import { csrfToken } from "../../../shared/csrf"

interface ProfileLabels {
  heading: string
  nameLabel: string
  noteLabel: string
  optional: string
  phoneLabel: string
  phoneHint: string
  whatsappLabel: string
  whatsappHint: string
  telegramLabel: string
  telegramHint: string
  telegramPlaceholder: string
  save: string
  noCardText: string
}

interface ProfilePillLabels {
  phone: string
  whatsapp: string
  telegram: string
}

interface ProfileProps {
  // Already the model's own display form (phone_text/whatsapp_text/
  // telegram_text) when a card exists; `null` on every field but `name`
  // when it does not (FR-2.2) -- `name` is then the user's own stored
  // name, a one-way prefill (FR-2.4).
  card: {
    name: string
    notes: string | null
    phone: string | null
    whatsapp: string | null
    telegram: string | null
  }
  cardExists: boolean
  // FR-4.3, NFR-6: the whole heading sentence, already composed
  // server-side ("Ваша карточка в «Контактах» · <section>") -- the
  // client never interpolates a section name into a template itself.
  sectionHeading: string
  pillLabels: ProfilePillLabels
  labels: ProfileLabels
  submitHref: string
}

// Flat, keyed by the model's bare attribute names (the landed
// AdminContactForm/AdminTripForm shape) -- the wire payload nests these
// under "contact" only at submit time, via `transform`.
interface ProfileFormData {
  name: string
  notes: string
  phone: string
  whatsapp: string
  telegram: string
  authenticity_token: string
}

const CARD_CLASSES = "rounded-xl border border-gray-200 p-3.5"
const CARD_CLASSES_NO_CARD = "rounded-xl border border-dashed border-gray-300 p-3.5"

const ACCENT_PILL_CLASSES =
  "inline-flex items-center rounded-full bg-accent/5 px-3.5 py-2 text-[13px] font-medium leading-[18px] text-accent"
const ACCENT_CALL_PILL_CLASSES = `${ACCENT_PILL_CLASSES} gap-1.5`
const GHOST_PILL_CLASSES =
  "inline-flex items-center rounded-full border border-dashed border-gray-300 bg-gray-100 px-[13px] py-[7px] text-[13px] font-medium leading-[18px] text-gray-400"
const GHOST_CALL_PILL_CLASSES = `${GHOST_PILL_CLASSES} gap-1.5`

const INPUT_CLASSES = "h-11 rounded-[10px] border border-gray-300 px-3 text-[15px] text-gray-900"
const PHONE_INPUT_CLASSES = `${INPUT_CLASSES} tabular-nums`
const TELEGRAM_INPUT_CLASSES = `${INPUT_CLASSES} placeholder:text-gray-400`
const LABEL_TEXT_CLASSES = "text-[13px] font-medium leading-[18px] text-gray-700"
const OPTIONAL_LABEL_CLASSES = "flex items-baseline gap-2 text-[13px] font-medium leading-[18px] text-gray-700"
const HINT_CLASSES = "text-xs leading-4 text-gray-400"
const ERROR_CLASSES = "text-xs leading-4 text-red-700"

// The call pill's own icon -- duplicated from Contacts.tsx's own
// (unexported) CallIcon rather than extracted to a shared module: two
// small, byte-identical copies is a cheaper cost than adding a new
// shared export for one four-line SVG (inertia-react-standards: extract
// only when it earns its keep).
function CallIcon() {
  return (
    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path
        d="M3 2.5h2.2L6.4 5.6l-1.5 1.2a10 10 0 004.3 4.3l1.2-1.5 3.1 1.2v2.2c0 .6-.5 1-1 1C7 14 2 9 2 3.5c0-.5.4-1 1-1z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  )
}

// FR-4.2: the page's one piece of client logic -- a channel pill is the
// accent pill iff its own input is non-blank after trimming whitespace,
// else the grey dashed ghost. Always a <span>, never a link (unlike
// /contacts' own ContactPill): no href is ever derived here, and the
// typed value itself is never shown or reformatted -- only the
// server-supplied label is.
function Pill({ label, value, icon = false }: { label: string; value: string; icon?: boolean }) {
  const accent = value.trim() !== ""
  const classes = icon
    ? accent
      ? ACCENT_CALL_PILL_CLASSES
      : GHOST_CALL_PILL_CLASSES
    : accent
      ? ACCENT_PILL_CLASSES
      : GHOST_PILL_CLASSES

  return (
    <span className={classes}>
      {icon && <CallIcon />}
      {label}
    </span>
  )
}

// `/profile` (Contacts::ProfileController#show renders it; #update
// saves it). The live card above the form is fed entirely from the
// form's own state (FR-4.1) -- typing in any field updates the preview
// in the same render, with no separate state to keep in sync. FR-1.5: no
// delete, no cancel -- one accent «Сохранить» is the form's only
// control. FR-3.3, FR-4.6: a refused save round-trips via the landed
// `redirect_to ..., inertia: { errors: }` idiom, so this component never
// unmounts -- `useForm` keeps every typed value and the preview keeps
// whichever pills were already accent; each field's own message renders
// in red beneath it, and `errors.user_id` (FR-6.2's uniqueness message,
// a field this page has no input for) renders as a red line directly
// above «Сохранить».
export default function Profile({ card, cardExists, sectionHeading, pillLabels, labels, submitHref }: ProfileProps) {
  const form = useForm<ProfileFormData>({
    name: card.name,
    notes: card.notes ?? "",
    phone: card.phone ?? "",
    whatsapp: card.whatsapp ?? "",
    telegram: card.telegram ?? "",
    authenticity_token: csrfToken(),
  })

  // FR-3.2: nests the five permitted fields under "contact" for
  // Contacts::ProfileController#update's params.expect -- the same
  // transform shape AdminContactForm/AdminTripForm already use.
  form.transform((data) => ({
    contact: {
      name: data.name, notes: data.notes, phone: data.phone,
      whatsapp: data.whatsapp, telegram: data.telegram,
    },
    authenticity_token: data.authenticity_token,
  }))

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    form.patch(submitHref)
  }

  // FR-6.2: `user_id` is never one of ProfileFormData's own fields (it
  // has no input on this page), so it is absent from useForm's own
  // `FormDataErrors<ProfileFormData>` type -- Inertia's shared `errors`
  // prop is a plain object keyed by whatever the server sent, so the one
  // cast below reads the one key this page renders without a field.
  const userLinkError = (form.errors as Record<string, string | undefined>).user_id

  return (
    <Layout>
      <h2 className="text-[22px] font-bold leading-7 text-gray-900">{labels.heading}</h2>

      <section className="flex flex-col gap-2.5" aria-live="polite">
        <h3 className="text-[13px] font-semibold leading-[18px] text-gray-500">{sectionHeading}</h3>
        <div className={cardExists ? CARD_CLASSES : CARD_CLASSES_NO_CARD}>
          <p className="text-[15px] font-semibold leading-[20px] text-gray-900">{form.data.name}</p>
          {/* FR-4.5: the note line echoes the field as typed; an empty
              note renders no line at all, exactly as on /contacts. Blank
              is judged after trimming, the same sense FR-4.2 uses for the
              pills and the server uses on save (a whitespace-only note is
              stripped to nothing and never shows on /contacts). */}
          {form.data.notes.trim() !== "" && (
            <p className="mt-0.5 text-[13px] leading-[18px] text-gray-500">{form.data.notes}</p>
          )}
          <div className="mt-2.5 flex flex-wrap gap-2">
            <Pill label={pillLabels.phone} value={form.data.phone} icon />
            <Pill label={pillLabels.whatsapp} value={form.data.whatsapp} />
            <Pill label={pillLabels.telegram} value={form.data.telegram} />
          </div>
        </div>
        {/* FR-4.4: the empty-state hint, dashed card only. */}
        {!cardExists && <p className={HINT_CLASSES}>{labels.noCardText}</p>}
      </section>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className={LABEL_TEXT_CLASSES}>{labels.nameLabel}</span>
          <input
            type="text"
            autoComplete="name"
            value={form.data.name}
            onChange={(event) => form.setData("name", event.target.value)}
            className={INPUT_CLASSES}
          />
          {form.errors.name && <span className={ERROR_CLASSES}>{form.errors.name}</span>}
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={OPTIONAL_LABEL_CLASSES}>
            {labels.noteLabel}
            <span className="text-xs font-normal text-gray-400">{labels.optional}</span>
          </span>
          <input
            type="text"
            value={form.data.notes}
            onChange={(event) => form.setData("notes", event.target.value)}
            className={INPUT_CLASSES}
          />
          {form.errors.notes && <span className={ERROR_CLASSES}>{form.errors.notes}</span>}
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={OPTIONAL_LABEL_CLASSES}>
            {labels.phoneLabel}
            <span className="text-xs font-normal text-gray-400">{labels.optional}</span>
          </span>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={form.data.phone}
            onChange={(event) => form.setData("phone", event.target.value)}
            className={PHONE_INPUT_CLASSES}
          />
          <span className={HINT_CLASSES}>{labels.phoneHint}</span>
          {form.errors.phone && <span className={ERROR_CLASSES}>{form.errors.phone}</span>}
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={OPTIONAL_LABEL_CLASSES}>
            {labels.whatsappLabel}
            <span className="text-xs font-normal text-gray-400">{labels.optional}</span>
          </span>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={form.data.whatsapp}
            onChange={(event) => form.setData("whatsapp", event.target.value)}
            className={PHONE_INPUT_CLASSES}
          />
          <span className={HINT_CLASSES}>{labels.whatsappHint}</span>
          {form.errors.whatsapp && <span className={ERROR_CLASSES}>{form.errors.whatsapp}</span>}
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={OPTIONAL_LABEL_CLASSES}>
            {labels.telegramLabel}
            <span className="text-xs font-normal text-gray-400">{labels.optional}</span>
          </span>
          <input
            type="text"
            placeholder={labels.telegramPlaceholder}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={form.data.telegram}
            onChange={(event) => form.setData("telegram", event.target.value)}
            className={TELEGRAM_INPUT_CLASSES}
          />
          <span className={HINT_CLASSES}>{labels.telegramHint}</span>
          {form.errors.telegram && <span className={ERROR_CLASSES}>{form.errors.telegram}</span>}
        </label>
        {/* FR-6.2: the user-link uniqueness refusal belongs to no visible
            field, so it renders here -- a red line directly above
            «Сохранить» -- rather than under any of the five fields
            above. */}
        {userLinkError && <span className={ERROR_CLASSES}>{userLinkError}</span>}
        <button
          type="submit"
          disabled={form.processing}
          className="mt-2 h-12 rounded-xl bg-accent text-[15px] font-semibold text-white disabled:opacity-60"
        >
          {labels.save}
        </button>
      </form>
    </Layout>
  )
}
